import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Sparkles, X, Loader2, Trophy, BadgeCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StarRating from "@/components/StarRating";
import SEO from "@/components/SEO";

const MAX = 3;

export default function ComparePage() {
  const [params, setParams] = useSearchParams();
  const slugs = params.getAll("p");
  const [providers, setProviders] = useState([]);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (slugs.length < 1) {
      setProviders([]);
      return;
    }
    setLoading(true);
    Promise.all(slugs.map((s) => api.get(`/providers/${s}`).then((r) => r.data).catch(() => null)))
      .then((items) => setProviders(items.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [params]);

  const runAI = async () => {
    if (providers.length < 2) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/compare-providers", {
        provider_ids: providers.map((p) => p.id),
      });
      setAi(data.ai);
    } finally {
      setAiLoading(false);
    }
  };

  const remove = (slug) => {
    const next = new URLSearchParams();
    slugs.filter((s) => s !== slug).forEach((s) => next.append("p", s));
    setParams(next);
    setAi(null);
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>;
  }

  if (providers.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Sparkles className="w-12 h-12 text-[#064E3B] mx-auto mb-4" />
        <h1 className="font-display text-3xl font-semibold mb-3">Compare IV Therapy Providers</h1>
        <p className="text-stone-600 mb-6">Add 2 or 3 providers from search results to see a side-by-side comparison with an AI recommendation.</p>
        <Button asChild className="bg-[#064E3B] hover:bg-[#053d2f] rounded-full"><Link to="/search">Browse providers →</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={`Compare ${providers.map((p) => p.name).join(" vs ")} | IV Therapy Finder`}
        description={`Side-by-side comparison of ${providers.length} IV therapy providers.`}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-1">Compare</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900">Side-by-side comparison</h1>
          <p className="text-stone-600 mt-1">{providers.length}/{MAX} providers · Add more from <Link to="/search" className="text-[#064E3B] font-semibold hover:underline">search results</Link></p>
        </div>
        <Button
          onClick={runAI}
          disabled={providers.length < 2 || aiLoading}
          className="bg-[#064E3B] hover:bg-[#053d2f] rounded-full"
          data-testid="compare-run-ai"
        >
          {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {ai ? "Re-run AI analysis" : "Get AI recommendation"}
        </Button>
      </div>

      {ai && (
        <div className="bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white rounded-2xl p-6 mb-6">
          <div className="text-xs uppercase tracking-wider text-white/70 mb-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> AI Analysis
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            {ai.summaries?.map((s, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-3">
                <div className="font-display font-semibold mb-1">{s.name}</div>
                <p className="text-xs text-white/90 leading-relaxed">{s.strength}</p>
              </div>
            ))}
          </div>
          {ai.recommendation && (
            <div className="bg-[#B4A081] text-[#0A2520] rounded-xl p-4 flex items-center gap-3">
              <Trophy className="w-6 h-6 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold">AI pick</div>
                <div className="font-display text-lg font-semibold">{ai.recommendation.name}</div>
                <div className="text-sm">{ai.recommendation.reason}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="relative aspect-[16/10] bg-stone-100">
              <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
              <button onClick={() => remove(p.slug)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-white flex items-center justify-center" data-testid={`compare-remove-${p.slug}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="font-display text-lg font-semibold leading-tight">{p.name}</h3>
                  {p.verified && <BadgeCheck className="w-4 h-4 text-[#10B981]" />}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{p.city}, {p.state}</div>
              </div>
              <StarRating value={p.avg_rating} count={p.review_count} />

              <Row label="Type" value={p.type === "both" ? "Mobile + Clinic" : p.type === "mobile" ? "Mobile IV" : "IV Clinic"} />
              <Row label="Years" value={`${p.years_in_business}+`} />
              <Row label="Services" value={`${p.services?.length || 0}`} />
              <Row label="Medical director" value={p.medical_director ? "✓" : "—"} />
              <Row label="Walk-ins" value={p.accepts_walkins ? "✓" : "—"} />
              <Row label="Home visits" value={p.home_visits ? "✓" : "—"} />
              <Row label="Insurance" value={p.accepts_insurance ? "✓" : "—"} />

              <Button asChild variant="outline" className="w-full mt-2">
                <Link to={`/listing/${p.slug}`}>View full profile <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm border-b border-stone-100 pb-1.5 last:border-0">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-900">{value}</span>
    </div>
  );
}
